import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle2, Clock, AlertTriangle, Download, FileText } from "lucide-react";

interface ChecklistItem {
  id: string;
  task: string;
  description: string;
  timing: 'voor' | 'tijdens' | 'na';
  priority: 'low' | 'medium' | 'high';
  responsible: 'klant' | 'installateur' | 'beiden';
}

const zonnepanelenChecklist: ChecklistItem[] = [
  {
    id: 'zp1',
    task: 'Dakconstructie laten keuren',
    description: 'Controleer of het dak het gewicht van zonnepanelen kan dragen',
    timing: 'voor',
    priority: 'high',
    responsible: 'klant'
  },
  {
    id: 'zp2',
    task: 'Elektrische aansluiting controleren',
    description: 'Meterkast en hoofdaansluiting geschikt voor omvormer',
    timing: 'voor',
    priority: 'high',
    responsible: 'installateur'
  },
  {
    id: 'zp3',
    task: 'Vergunningen aanvragen',
    description: 'Check of welstandsvergunning nodig is bij gemeente',
    timing: 'voor',
    priority: 'medium',
    responsible: 'beiden'
  },
  {
    id: 'zp4',
    task: 'Verzekering informeren',
    description: 'Meld de installatie bij uw opstalverzekering',
    timing: 'voor',
    priority: 'medium',
    responsible: 'klant'
  },
  {
    id: 'zp5',
    task: 'Werkplek voorbereiden',
    description: 'Materialen opslag en toegang tot dak vrijmaken',
    timing: 'tijdens',
    priority: 'high',
    responsible: 'klant'
  },
  {
    id: 'zp6',
    task: 'Veiligheidsmaatregelen',
    description: 'Installateur gebruikt persoonlijke beschermingsmiddelen',
    timing: 'tijdens',
    priority: 'high',
    responsible: 'installateur'
  },
  {
    id: 'zp7',
    task: 'Opname eindresultaat',
    description: 'Foto\'s maken van installatie en omvormer instellingen',
    timing: 'tijdens',
    priority: 'medium',
    responsible: 'installateur'
  },
  {
    id: 'zp8',
    task: 'Systeemtest uitvoeren',
    description: 'Volledige werking testen en performance controleren',
    timing: 'tijdens',
    priority: 'high',
    responsible: 'installateur'
  },
  {
    id: 'zp9',
    task: 'Garantiepapieren overhandigen',
    description: 'Alle garantiebewijzen en handleidingen doornemen',
    timing: 'na',
    priority: 'high',
    responsible: 'installateur'
  },
  {
    id: 'zp10',
    task: 'Monitoring app installeren',
    description: 'Opbrengst monitoring systeem activeren en uitleggen',
    timing: 'na',
    priority: 'medium',
    responsible: 'beiden'
  },
  {
    id: 'zp11',
    task: 'Netcode registratie',
    description: 'Installatie registreren bij netbeheerder voor saldering',
    timing: 'na',
    priority: 'high',
    responsible: 'installateur'
  },
  {
    id: 'zp12',
    task: 'Jaarlijkse onderhoudsinspectie',
    description: 'Afspraak maken voor jaarlijkse controle en reiniging',
    timing: 'na',
    priority: 'low',
    responsible: 'klant'
  }
];

const warmtepompChecklist: ChecklistItem[] = [
  {
    id: 'wp1',
    task: 'Energielabel woning bepalen',
    description: 'Isolatie en energieprestatie woning geschikt voor warmtepomp',
    timing: 'voor',
    priority: 'high',
    responsible: 'beiden'
  },
  {
    id: 'wp2',
    task: 'Verwarmingssysteem controleren',
    description: 'Radiatoren en leidingen geschikt voor lage temperatuur',
    timing: 'voor',
    priority: 'high',
    responsible: 'installateur'
  },
  {
    id: 'wp3',
    task: 'Plaatsing buitenunit bepalen',
    description: 'Locatie met voldoende ventilatie en weinig geluidsoverlast',
    timing: 'voor',
    priority: 'high',
    responsible: 'beiden'
  },
  {
    id: 'wp4',
    task: 'Elektrische aansluiting voorbereiden',
    description: 'Driefasenaansluiting en extra groep in meterkast',
    timing: 'voor',
    priority: 'high',
    responsible: 'installateur'
  },
  {
    id: 'wp5',
    task: 'ISDE subsidie aanvragen',
    description: 'Aanvraag indienen voor Investeringssubsidie Duurzame Energie',
    timing: 'voor',
    priority: 'medium',
    responsible: 'klant'
  },
  {
    id: 'wp6',
    task: 'Oude CV-ketel loskoppelen',
    description: 'Bestaande verwarmingssysteem veilig buiten bedrijf stellen',
    timing: 'tijdens',
    priority: 'high',
    responsible: 'installateur'
  },
  {
    id: 'wp7',
    task: 'Leidingwerk aanpassen',
    description: 'Koppelingen maken tussen binnen- en buitenunit',
    timing: 'tijdens',
    priority: 'high',
    responsible: 'installateur'
  },
  {
    id: 'wp8',
    task: 'Systeem spoelen en vullen',
    description: 'Complete installatie doorspoeelen en druktest uitvoeren',
    timing: 'tijdens',
    priority: 'high',
    responsible: 'installateur'
  },
  {
    id: 'wp9',
    task: 'Inregeling en optimalisatie',
    description: 'Warmtepomp instellingen optimaliseren voor uw woning',
    timing: 'na',
    priority: 'high',
    responsible: 'installateur'
  },
  {
    id: 'wp10',
    task: 'Gebruikersinstructie',
    description: 'Uitleg bediening, onderhoud en energiebesparende tips',
    timing: 'na',
    priority: 'high',
    responsible: 'installateur'
  }
];

export function InstallationChecklist() {
  const [completedTasks, setCompletedTasks] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState('zonnepanelen');

  const toggleTask = (taskId: string) => {
    const newCompleted = new Set(completedTasks);
    if (newCompleted.has(taskId)) {
      newCompleted.delete(taskId);
    } else {
      newCompleted.add(taskId);
    }
    setCompletedTasks(newCompleted);
  };

  const getChecklist = () => {
    return activeTab === 'zonnepanelen' ? zonnepanelenChecklist : warmtepompChecklist;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'default';
    }
  };

  const getTimingIcon = (timing: string) => {
    switch (timing) {
      case 'voor': return <Clock className="w-4 h-4" />;
      case 'tijdens': return <AlertTriangle className="w-4 h-4" />;
      case 'na': return <CheckCircle2 className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const progress = (completedTasks.size / getChecklist().length) * 100;

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center gap-2">
          <FileText className="w-6 h-6 text-primary" />
          <h2 className="text-2xl font-bold">Installatie Checklist</h2>
        </div>
        <p className="text-muted-foreground">
          Stap voor stap gids voor een succesvolle installatie
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="zonnepanelen">Zonnepanelen</TabsTrigger>
          <TabsTrigger value="warmtepomp">Warmtepomp</TabsTrigger>
        </TabsList>

        <TabsContent value="zonnepanelen" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Zonnepanelen Installatie</CardTitle>
              <CardDescription>
                Complete checklist voor een professionele zonnepanelen installatie
              </CardDescription>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Voortgang</span>
                  <span>{completedTasks.size}/{zonnepanelenChecklist.length}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            </CardHeader>
          </Card>
        </TabsContent>

        <TabsContent value="warmtepomp" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Warmtepomp Installatie</CardTitle>
              <CardDescription>
                Complete checklist voor een professionele warmtepomp installatie
              </CardDescription>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Voortgang</span>
                  <span>{completedTasks.size}/{warmtepompChecklist.length}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            </CardHeader>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="space-y-4">
        {['voor', 'tijdens', 'na'].map((timing) => (
          <Card key={timing}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 capitalize">
                {getTimingIcon(timing)}
                {timing} installatie
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {getChecklist()
                .filter(item => item.timing === timing)
                .map((item) => (
                  <div 
                    key={item.id} 
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                      completedTasks.has(item.id) 
                        ? 'bg-primary/5 border-primary/20' 
                        : 'bg-card border-border'
                    }`}
                  >
                    <Checkbox
                      id={item.id}
                      checked={completedTasks.has(item.id)}
                      onCheckedChange={() => toggleTask(item.id)}
                      className="mt-1"
                    />
                    <div className="flex-1 space-y-2">
                      <div className="flex items-start justify-between">
                        <label 
                          htmlFor={item.id}
                          className={`font-medium cursor-pointer ${
                            completedTasks.has(item.id) 
                              ? 'line-through text-muted-foreground' 
                              : ''
                          }`}
                        >
                          {item.task}
                        </label>
                        <div className="flex gap-2">
                          <Badge variant={getPriorityColor(item.priority) as any}>
                            {item.priority}
                          </Badge>
                          <Badge variant="outline">
                            {item.responsible}
                          </Badge>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <Download className="w-5 h-5 text-primary" />
            <div className="flex-1">
              <h3 className="font-medium">Download Printbare Versie</h3>
              <p className="text-sm text-muted-foreground">
                Download de checklist als PDF voor gebruik tijdens de installatie
              </p>
            </div>
            <Button variant="outline">
              Download PDF
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}